import { useState, useEffect } from "react";
import { AdminLayout } from "../../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Users, UserCheck, AlertTriangle, UserX, CreditCard, BarChart3, BookOpen, Calendar, Calculator } from "lucide-react";
import axios from "axios";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ["#FACC15", "#22C55E", "#3B82F6", "#EF4444", "#8B5CF6", "#EC4899", "#14B8A6"];

const StatCard = ({ title, value, icon: Icon, variant = "default" }) => {
  const variants = {
    default: "text-foreground",
    success: "text-green-500",
    warning: "text-yellow-500",
    danger: "text-red-500",
    primary: "text-primary"
  };

  return (
    <Card className="bg-card border-border">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-widest mb-2">{title}</p>
            <p className={`text-3xl font-bold ${variants[variant]}`}>{value}</p>
          </div>
          <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${
            variant === "success" ? "bg-green-500/10" :
            variant === "warning" ? "bg-yellow-500/10" :
            variant === "danger" ? "bg-red-500/10" :
            variant === "primary" ? "bg-primary/10" :
            "bg-secondary"
          }`}>
            <Icon className={`h-6 w-6 ${variants[variant]}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  const bookingCounts = stats?.booking_counts || {};
  const bookingData = [
    { name: "BQ-Abo", value: bookingCounts.bq_abo || 0 },
    { name: "HQ-Abo", value: bookingCounts.hq_abo || 0 },
    { name: "BQ-Intensiv", value: bookingCounts.bq_intensiv || 0 },
    { name: "HQ-Intensiv", value: bookingCounts.hq_intensiv || 0 },
    { name: "Probetraining", value: bookingCounts.probetraining || 0 },
    { name: "Mathe", value: bookingCounts.mathe_grundkurs || 0 },
    { name: "Fachgespräch", value: bookingCounts.fachgespraech || 0 },
  ].filter(d => d.value > 0);

  const hqSubCounts = stats?.hq_sub_counts || {};
  const hqSubData = [
    { name: "HQ Abo Metalltechnik", value: hqSubCounts.metall || 0 },
  ].filter(d => d.value > 0);

  const chartData = stats?.monthly_stats || [];

  return (
    <AdminLayout title="Dashboard">
      <div className="space-y-8" data-testid="admin-dashboard">
        {/* Main Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard 
            title="Teilnehmer gesamt" 
            value={stats?.total_participants || 0} 
            icon={Users} 
            variant="primary"
          />
          <StatCard 
            title="Aktive Abos" 
            value={stats?.active_abos || 0} 
            icon={UserCheck} 
            variant="success"
          />
          <StatCard 
            title="Bald ablaufend" 
            value={stats?.expiring_abos || 0} 
            icon={AlertTriangle} 
            variant="warning"
          />
          <StatCard 
            title="Abgelaufen" 
            value={stats?.expired_abos || 0} 
            icon={UserX} 
            variant="danger"
          />
        </div>

        {/* Booking Type Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <CreditCard className="h-6 w-6 text-primary mx-auto mb-2" />
              <p className="text-xs text-muted-foreground">BQ-Abo</p>
              <p className="text-xl font-bold">{bookingCounts.bq_abo || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <CreditCard className="h-6 w-6 text-blue-500 mx-auto mb-2" />
              <p className="text-xs text-muted-foreground">HQ-Abo</p>
              <p className="text-xl font-bold">{bookingCounts.hq_abo || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <BookOpen className="h-6 w-6 text-green-500 mx-auto mb-2" />
              <p className="text-xs text-muted-foreground">BQ-Intensiv</p>
              <p className="text-xl font-bold">{bookingCounts.bq_intensiv || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <BookOpen className="h-6 w-6 text-purple-500 mx-auto mb-2" />
              <p className="text-xs text-muted-foreground">HQ-Intensiv</p>
              <p className="text-xl font-bold">{bookingCounts.hq_intensiv || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <Calendar className="h-6 w-6 text-orange-500 mx-auto mb-2" />
              <p className="text-xs text-muted-foreground">Probetraining</p>
              <p className="text-xl font-bold">{bookingCounts.probetraining || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <Calculator className="h-6 w-6 text-pink-500 mx-auto mb-2" />
              <p className="text-xs text-muted-foreground">Mathe</p>
              <p className="text-xl font-bold">{bookingCounts.mathe_grundkurs || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <Users className="h-6 w-6 text-teal-500 mx-auto mb-2" />
              <p className="text-xs text-muted-foreground">Fachgespräch</p>
              <p className="text-xl font-bold">{bookingCounts.fachgespraech || 0}</p>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Booking Distribution */}
          <Card className="bg-card border-border">
            <CardHeader className="border-b border-border">
              <CardTitle className="flex items-center gap-2 text-lg">
                <BarChart3 className="h-5 w-5 text-primary" />
                Buchungsverteilung
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="h-64">
                {bookingData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={bookingData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {bookingData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#09090b', 
                          border: '1px solid #27272a',
                          borderRadius: '4px',
                          color: '#fff'
                        }}
                      />
                      <Legend 
                        formatter={(value) => <span style={{ color: '#fff' }}>{value}</span>}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-muted-foreground">
                    Keine Buchungen vorhanden
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Monthly Trend */}
          <Card className="bg-card border-border">
            <CardHeader className="border-b border-border">
              <CardTitle className="flex items-center gap-2 text-lg">
                <BarChart3 className="h-5 w-5 text-primary" />
                Neue Buchungen (6 Monate)
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <XAxis 
                      dataKey="month" 
                      tick={{ fill: '#a1a1aa', fontSize: 12 }}
                      axisLine={{ stroke: '#27272a' }}
                      tickLine={false}
                    />
                    <YAxis 
                      tick={{ fill: '#a1a1aa', fontSize: 12 }}
                      axisLine={{ stroke: '#27272a' }}
                      tickLine={false}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#09090b', 
                        border: '1px solid #27272a',
                        borderRadius: '4px',
                        color: '#fff'
                      }}
                    />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                      {chartData.map((_, index) => (
                        <Cell key={index} fill="#FACC15" />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* HQ Sub-Types & Fachrichtungen */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* HQ Sub-Types */}
          {hqSubData.length > 0 && (
            <Card className="bg-card border-border">
              <CardHeader className="border-b border-border">
                <CardTitle className="text-lg">HQ-Abo Fachrichtungen</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  {hqSubData.map((item, index) => (
                    <div key={item.name} className="flex items-center justify-between">
                      <span className="text-muted-foreground">{item.name}</span>
                      <div className="flex items-center gap-3">
                        <div className="w-32 h-2 bg-secondary rounded-full overflow-hidden">
                          <div 
                            className="h-full rounded-full" 
                            style={{ 
                              width: `${(item.value / Math.max(...hqSubData.map(d => d.value))) * 100}%`,
                              backgroundColor: COLORS[index]
                            }}
                          />
                        </div>
                        <span className="font-bold w-8 text-right">{item.value}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Industriemeister Fachrichtungen */}
          {stats?.fachrichtung_counts && Object.keys(stats.fachrichtung_counts).length > 0 && (
            <Card className="bg-card border-border">
              <CardHeader className="border-b border-border">
                <CardTitle className="text-lg">Industriemeister-Fachrichtungen</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {Object.entries(stats.fachrichtung_counts)
                    .sort((a, b) => b[1] - a[1])
                    .map(([name, count], index) => (
                      <div key={name} className="flex items-center justify-between">
                        <span className="text-muted-foreground truncate">{name}</span>
                        <span className="font-bold ml-2">{count}</span>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default Dashboard;
